// src/services/http.ts
//
// Shared helpers used by every service module.
//
//   - ApiError: a typed error that carries the HTTP status, a human-readable
//     message (preferring the backend `message` field when available), and the
//     raw response body. Components catch this to render server-supplied
//     error text instead of a generic "something went wrong".
//
//   - handleJson: parses the JSON body and throws ApiError on non-2xx. It
//     does NOT inspect any `success` flag — that varies per endpoint (string
//     "1", number 1, boolean true, capital S, sometimes absent) and is the
//     service function's responsibility to interpret.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly data?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Accepts either a Response or a Promise<Response> so services can write
// `handleJson(apiFetch(...))` without an inner `await`.
export async function handleJson<T = any>(
  input: Response | Promise<Response>
): Promise<T> {
  const response = input instanceof Response ? input : await input;

  let data: any = null;
  try {
    data = await response.json();
  } catch {
    // Empty body or non-JSON is fine for 204 / some error pages.
  }

  if (!response.ok) {
    const message =
      (data && typeof data.message === "string" && data.message) ||
      `Request failed (${response.status})`;
    throw new ApiError(response.status, message, data);
  }

  return data as T;
}
